import z from 'zod';

import { ItemStatusEnum } from '@/modules/common';
import { FileObject, OptionType } from '@/modules/common/types';
import { Mask } from '@/modules/masks/types';
import { Model } from '@/modules/models/types';
import { OverlayItem } from '@/modules/overlays/types';

import {
  EventAiModelType,
  EventDetailsTab,
  EventModelPosition,
  EventParticipantStatus,
  EventPrinterStatus,
  EventStatusEnum,
} from '../constants';
import { stationsSchema } from '../validations';

export enum EventQueryKeys {
  Stations = 'eventStations',
}

export interface Event {
  id: string;
  name: string;
  startDate: string;
  aiModel: string;
  status: ItemStatusEnum;
}

export interface EventType {
  id: string;
  name: string;
  tabs: EventDetailsTab[];
  requiredFields: {
    [key: string]: string[];
  };
}
export interface EventsResponse {
  items: Event[];
}

export interface EventUploadUrlData {
  eventId: string;
  signedUrl: string;
}

export interface EventUploadUrlsData {
  eventId: string;
  signedUrl: string;
  idleStateVideoPublicUrl: string;
}

export interface EventPrinter {
  name: string;
  type: string;
  printerId: string;
  status: EventPrinterStatus;
}

export interface EventModelSize {
  quantity: string;
  sizeId: string;
}

export interface EventStyle {
  id: string;
  name: string;
  imagePublicUrl: string;
}

export interface EventModelDetails {
  model: Model;
  sizes: {
    quantity: string;
    size: OptionType;
  }[];
  sides: EventModelPosition[];
}

export interface EventMask extends Mask {
  overlays: OverlayItem;
}

export interface EventDetails extends Event {
  publicUrl: string;
  idleStateVideoPublicUrl: string;
  gptEnabled: boolean;
  flagsAndInitialsEnabled: boolean;
  signatureEnabled: boolean;
  electricEffectEnabled: boolean;
  placementFeatureEnabled: boolean;
  printers?: EventPrinter[];
  desktopSecretKey: string;
  iosSecretKey: string;
  models: EventModelDetails[];
  masks: EventMask[];
  styles: EventStyle[];
  videoFileObject: FileObject | null;
  idleStateVideoFileObject: FileObject | null;
  types: EventType[];
  validationErrors: Record<string, string[]>;
}

export interface CreateEventPayload {
  id: string;
  name: string;
  startDate: string;
  types: { typeId: string }[];
}

export interface UpdateEventPayload {
  id: string;
  status: ItemStatusEnum;
  name: string;
  aiModel: string;
  startDate: string;
  gptEnabled: boolean;
  idleStateVideoPublicUrl: string;
  flagsAndInitialsEnabled: boolean;
  electricEffectEnabled: boolean;
  placementFeatureEnabled: boolean;
  printers?: EventPrinter[];
  signatureEnabled?: boolean;
  models: {
    modelId: string;
    sizes: EventModelSize[];
    sides: EventModelPosition[];
  }[];
  styles: {
    styleId: string;
  }[];
  masks: {
    maskId: string;
    overlays: { overlayId: string }[];
  }[];
  types: {
    typeId: string;
  }[];
}

export interface EventParticipant {
  id: string;
  firstName: string;
  lastName: string;
  usagesLeft: number;
  email: string;
  company: string;
  jobTitle: string;
  phoneNumber: string;
  comment: string;
  type: string;
  externalId?: string;
  status: EventParticipantStatus;
  notificationNumbers: string[];
}

export interface EventParticipantsResponse {
  items: EventParticipant[];
}

export interface EventProductResponseItem {
  id: string;
  name: string;
  category: string;
  nickname: string;
  styleNumber: string;
  totalAmount: number;
  sizes: {
    id: string;
    size: string;
    amount: number;
  }[];
  createdAt: string;
}

export interface EventProduct extends Omit<EventProductResponseItem, 'sizes'> {
  size: {
    id: string;
    size: string;
    amount: number;
  };
}

export interface EventProductsResponse {
  items: EventProductResponseItem[];
}
export interface EventParticipantType {
  eventId: string;
  id: string;
  value: string;
  notificationNumbers: {
    id: string;
    phoneNumber: string;
    typeId: string;
  }[];
}

export interface CreateEventParticipantTypePayload {
  types: {
    value: string;
    phoneNumbers: string[];
  }[];
}

export interface EventStationResponseItem {
  id: string;
  name: string;
  accessTypes: {
    id: string;
    name: string;
  }[];
}

export interface MutateStationPayload {
  id: string;
  name: string;
  accessTypeIds: string[];
}

export type StationsFormValues = z.infer<typeof stationsSchema>;

export interface SendSmsPayload {
  message: string;
  participantIds: string[];
}

export interface SendOrderSmsPayload {
  message: string;
  orderIds: string[];
}

export enum EventOrderStatus {
  New = 'NEW',
  Pending = 'PENDING',
  Printing = 'PRINTING',
  Completed = 'COMPLETED',
  Canceled = 'CANCELED',
  Failed = 'FAILED',
}

export enum EventSmsStatus {
  Sent = 'sent',
  Failed = 'failed',
}

export interface EventOrder {
  id: string;
  name: string;
  color: string;
  createdAt: Date;
  firstName: string;
  gender: string;
  imageUrl: string;
  lastName: string;
  number: number;
  phoneNumber: string;
  size: string;
  status: EventOrderStatus;
  type: string;
  updatedAt: Date;
}

export interface EventSms {
  message: string;
  status: EventSmsStatus;
  phoneNumber: 'string';
  createdAt: Date;
}
